import { Page } from "./PagesInterface";
import { MiscTaskQueue } from "./MiscTaskApprovalInterface";

const MISC_TASK_QUEUES: ReadonlyArray<MiscTaskQueue> = ['firstLevel', 'secondLevel', 'approved', 'denied'];

export interface DeepLinkParams {
  page?: Page;
  requestId?: number;
  /** Selfie Exceptions: open this exception's detail on arrival. */
  exceptionId?: number;
  /**
   * Selfie Exceptions / Misc Tasks: select this pay period, since the target may
   * not be in the current one (the daily reds emails link each row's week here).
   */
  payPeriodID?: number;
  /** Timesheet Exceptions: highlight this event on arrival (alert email CTA). */
  eventId?: number;
  /** Misc Tasks: open this bucket on arrival (TAS-544 approver email). */
  queue?: MiscTaskQueue;
  /**
   * Employees roster: open this employee's Enrollment Photo modal on arrival
   * (TAS-568 failure email / Fix enrollment). The employee's altEmployeeID.
   */
  photo?: string;
}

export function parseDeepLinkParams(): DeepLinkParams {
  const params: DeepLinkParams = {};
  const search = window.location.search;
  if (!search) return params;

  const urlParams = new URLSearchParams(search);

  const pageParam = urlParams.get("page");
  if (pageParam) {
    // Validate it's a known Page enum value
    const pageValues = Object.values(Page) as string[];
    if (pageValues.includes(pageParam)) {
      params.page = pageParam as Page;
    }
  }

  const requestIdParam = urlParams.get("requestId");
  if (requestIdParam) {
    const id = parseInt(requestIdParam, 10);
    if (!isNaN(id)) {
      params.requestId = id;
    }
  }

  const exceptionIdParam = urlParams.get("exceptionId");
  if (exceptionIdParam) {
    const id = parseInt(exceptionIdParam, 10);
    if (!isNaN(id)) {
      params.exceptionId = id;
    }
  }

  // The alert email sends `event`; `eventId` is accepted too so a hand-edited or
  // forwarded link with either spelling still lands on the right row.
  const eventIdParam = urlParams.get("event") ?? urlParams.get("eventId");
  if (eventIdParam) {
    const id = parseInt(eventIdParam, 10);
    if (!isNaN(id)) {
      params.eventId = id;
    }
  }

  const payPeriodIDParam = urlParams.get("payPeriodID");
  if (payPeriodIDParam) {
    const id = parseInt(payPeriodIDParam, 10);
    if (!isNaN(id)) {
      params.payPeriodID = id;
    }
  }

  const photoParam = urlParams.get("photo");
  if (photoParam && photoParam.trim()) {
    params.photo = photoParam.trim();
  }

  const queueParam = urlParams.get("queue");
  if (queueParam && (MISC_TASK_QUEUES as ReadonlyArray<string>).includes(queueParam)) {
    params.queue = queueParam as MiscTaskQueue;
  }

  return params;
}

/**
 * A module-level copy of the deep link, read once by whichever page consumes it.
 *
 * SiteContext's `pendingDeepLink` is the primary channel, but `useAdminSession`
 * calls `setPage(...)` and `setPendingDeepLink(null)` inside the same effect and
 * React batches those — so a target page can mount with the context value
 * already cleared, depending on flush order. This stash is unaffected by that
 * timing. Both channels are read (context first) behind a one-shot ref guard.
 *
 * Deliberately not a fix to `useAdminSession`: changing when it clears
 * `pendingDeepLink` would alter Time Off Approvals' consumption timing too.
 */
let stashedDeepLink: DeepLinkParams | null = null;

export function stashDeepLink(params: DeepLinkParams): void {
  stashedDeepLink = params;
}

/** Reads the stashed params WITHOUT consuming them (usePayPeriods uses this to pick the initial week). */
export function peekStashedDeepLink(): DeepLinkParams | null {
  return stashedDeepLink;
}

/** Returns the stashed params once, then forgets them. */
export function consumeStashedDeepLink(): DeepLinkParams | null {
  const stashed = stashedDeepLink;
  stashedDeepLink = null;
  return stashed;
}
